import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { AlertCircle, Loader2, ShieldAlert, TrendingUp, Zap, Activity, Printer } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { fmtKwp, GBP, fmtDateGB, fmtDateTimeGB, ukShort } from "../lib/format";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Public investor snapshot — read-only, driven by a signed share token
 * from `/share/investor/:token`. No auth. The server enforces the token,
 * redaction mode, and revocation/expiry. Live production-vs-forecast
 * sparklines render across the last 12 months + rolling 30 days.
 * Org branding (logo + accent colour) is pulled from `/api/public/branding`.
 */
export default function PublicInvestorShare() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [branding, setBranding] = useState({ org_name: "FLOW Energy App", accent_color: null, logo_url: null });

  useEffect(() => {
    let alive = true;
    (async () => {
      // Fetch branding + snapshot in parallel — branding is best-effort.
      const brandingPromise = axios.get(`${BACKEND}/api/public/branding`)
        .then(r => r.data).catch(() => null);
      try {
        const [b, { data }] = await Promise.all([
          brandingPromise,
          axios.get(`${BACKEND}/api/public/investor-share/${token}`),
        ]);
        if (!alive) return;
        if (b) setBranding({ ...branding, ...b });
        setState({ loading: false, data, error: null });
      } catch (e) {
        const status = e.response?.status;
        const detail = e.response?.data?.detail;
        const message =
          status === 410 ? "This share link has been revoked or has expired."
          : status === 404 ? "Share link not found."
          : detail || "Unable to load this snapshot right now.";
        if (alive) setState({ loading: false, data: null, error: { status, message } });
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const accent = branding.accent_color || "#00D6DF";

  if (state.loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg)]" data-testid="share-loading">
      <div className="text-center">
        <Loader2 className="animate-spin inline text-[var(--accent)]" size={22} />
        <div className="mt-2 muted text-sm">Loading snapshot…</div>
      </div>
    </div>
  );

  if (state.error) return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg)] px-4">
      <div className="max-w-md text-center bg-white border border-[var(--line)] rounded-lg p-8" data-testid="share-error">
        <AlertCircle size={28} className="inline mb-3" style={{ color: "#C0362C" }} />
        <div className="font-display text-xl font-semibold mb-2">Unavailable</div>
        <p className="text-sm muted mb-4">{state.error.message}</p>
        <p className="text-[11px] muted">If you were expecting to see live data, ask the person who sent you this link to create a new one.</p>
      </div>
    </div>
  );

  const d = state.data;
  const share = d.share;
  const isFull = share.mode === "full";
  const isProject = share.scope === "project";
  const gbp = (v) => GBP.format(Number(v) || 0);

  return (
    <div className="min-h-screen bg-[var(--bg)]" data-testid="share-view">
      {/* Print-optimised CSS — single-page A4-friendly layout so LPs can
          hit "Save as PDF" from any browser and get a clean artifact. */}
      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          html, body { background: #ffffff !important; }
          .print\\:hidden { display: none !important; }
          [data-testid="share-view"] { min-height: 0 !important; background: #ffffff !important; }
          [data-testid="share-view"] > div:first-child {
            background: ${accent} !important;
            color: #ffffff !important;
            border-bottom: none !important;
          }
          [data-testid="share-view"] table { page-break-inside: avoid; }
          [data-testid="share-view"] .recharts-wrapper { page-break-inside: avoid; }
          [data-testid="share-series"] > div { break-inside: avoid; }
          [data-testid="share-stages"], [data-testid="share-ams"] {
            box-shadow: none !important;
          }
        }
      `}</style>

      {/* Header */}
      <div style={{ background: "#314041" }} className="text-white">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between" style={{ borderBottom: `4px solid ${accent}` }}>
          <div className="flex items-center gap-4">
            {/* Header is always dark slate (both themes): default to the cyan + white mark;
                a custom org logo sits on a white pill so dark artwork stays legible. */}
            {branding.logo_url ? (
              <span className="logo-pill rounded-md px-2.5 py-1.5 inline-flex bg-[#fff]" data-testid="share-brand-logo-pill">
                <img
                  src={branding.logo_url.startsWith("http") ? branding.logo_url : `${BACKEND}${branding.logo_url}`}
                  alt={branding.org_name}
                  className="h-8 w-auto"
                  onError={(e) => { e.currentTarget.src = "/brand/eneprox-logo.png"; }}
                  data-testid="share-brand-logo"
                />
              </span>
            ) : (
              <img src="/brand/eneprox-logo-dark.png" alt="eneprox" className="h-9 w-auto" data-testid="share-brand-logo" />
            )}
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/70 font-semibold">
                {branding.org_name} · Investor snapshot
              </div>
              <div className="font-display text-2xl font-bold tracking-tight mt-1">
                {isProject ? d.project?.name : "Portfolio overview"}
              </div>
              {share.label && <div className="text-xs text-white/70 mt-1">{share.label}</div>}
            </div>
          </div>
          <div className="text-right text-[11px] text-white/70">
            <div className="uppercase tracking-wider">Mode</div>
            <div className="mt-1 flex items-center gap-1 justify-end">
              {isFull ? <TrendingUp size={12} /> : <ShieldAlert size={12} />}
              <span className="font-mono">{isFull ? "Full mirror" : "Production only"}</span>
            </div>
            {share.expires_at && (
              <div className="text-[10px] mt-1 opacity-70">Expires {fmtDateGB(share.expires_at)}</div>
            )}
            <button
              type="button"
              onClick={() => window.print()}
              className="print:hidden mt-3 inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded text-white text-[11px] font-medium transition-colors"
              data-testid="share-print-btn"
            >
              <Printer size={12} /> Print / Save PDF
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        {/* Performance sparklines (production vs weather forecast) */}
        <PerformanceSeries series={d.series} accent={accent} />

        {/* Portfolio scope */}
        {!isProject && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <StatCard label="Total projects" value={d.portfolio_total_projects} icon={<Zap size={14} />} />
              <StatCard label="Portfolio capacity" value={fmtKwp(d.portfolio_total_kwp)} icon={<TrendingUp size={14} />} />
            </div>

            <div className="bg-white border border-[var(--line)] rounded-md overflow-hidden">
              <div className="px-5 py-3 border-b border-[var(--line)]">
                <div className="label">By lifecycle stage</div>
              </div>
              <table className="data-table w-full" data-testid="share-stages">
                <thead>
                  <tr>
                    <th>Stage</th>
                    <th className="text-right">Projects</th>
                    <th className="text-right">Capacity</th>
                    <th className="text-right">Avg current</th>
                    <th className="text-right">SLA</th>
                    <th className="text-right">Overrun</th>
                  </tr>
                </thead>
                <tbody>
                  {(d.stages || []).map(s => (
                    <tr key={s.key}>
                      <td>{s.label}</td>
                      <td className="text-right font-mono">{s.count}</td>
                      <td className="text-right font-mono">{fmtKwp(s.total_kwp)}</td>
                      <td className="text-right font-mono muted">{s.avg_current_days == null ? "—" : `${s.avg_current_days.toFixed(1)}d`}</td>
                      <td className="text-right font-mono muted">{s.sla_days == null ? "—" : `${s.sla_days}d`}</td>
                      <td className="text-right font-mono" style={{ color: s.overrun_count ? "#C0362C" : "inherit" }}>{s.overrun_count || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="bg-white border border-[var(--line)] rounded-md overflow-hidden">
              <div className="px-5 py-3 border-b border-[var(--line)]">
                <div className="label">By asset manager</div>
              </div>
              <table className="data-table w-full" data-testid="share-ams">
                <thead>
                  <tr>
                    <th>Asset Manager</th>
                    {isFull && <th>Email</th>}
                    <th className="text-right">Projects</th>
                    <th className="text-right">Capacity</th>
                  </tr>
                </thead>
                <tbody>
                  {(d.by_asset_manager || []).map((am, i) => (
                    <tr key={i}>
                      <td>{am.asset_manager_name}</td>
                      {isFull && <td className="font-mono text-xs muted">{am.email}</td>}
                      <td className="text-right font-mono">{am.project_count}</td>
                      <td className="text-right font-mono">{fmtKwp(am.total_kwp)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Project scope */}
        {isProject && d.project && (
          <>
            <div className="bg-white border border-[var(--line)] rounded-md p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard label="System size" value={fmtKwp(d.project.system_size_kwp || 0)} />
                <StatCard label="Stage" value={d.project.lifecycle_status || d.project.status || "—"} />
                <StatCard label="Handover" value={fmtDateGB(d.project.handover_date)} />
                <StatCard label="Installed" value={fmtDateGB(d.project.installation_date)} />
              </div>
              <div className="mt-4 text-xs muted">
                {d.project.address}{d.project.postcode ? `, ${d.project.postcode}` : ""}
              </div>
              {isFull && d.project.capex_per_kwp_gbp != null && (
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <StatCard label="CapEx / kWp" value={gbp(d.project.capex_per_kwp_gbp)} />
                  <StatCard label="OpEx / kWp / yr" value={gbp(d.project.opex_per_kwp_gbp || 0)} />
                </div>
              )}
            </div>

            <div className="bg-white border border-[var(--line)] rounded-md overflow-hidden">
              <div className="px-5 py-3 border-b border-[var(--line)]">
                <div className="label">Lifecycle progress</div>
              </div>
              <table className="data-table w-full">
                <thead>
                  <tr><th>Stage</th><th className="text-right">In this stage</th></tr>
                </thead>
                <tbody>
                  {(d.stages || []).map(s => (
                    <tr key={s.key}>
                      <td>{s.label}</td>
                      <td className="text-right font-mono">{s.count > 0 ? "current" : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <div className="text-center text-[11px] muted pt-4 pb-2">
          Snapshot generated {fmtDateTimeGB(d.generated_at)}. Refresh to pull the latest.
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon }) {
  return (
    <div className="bg-white border border-[var(--line)] rounded-md p-4">
      <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--ink-2)] font-semibold flex items-center gap-1.5">
        {icon}{label}
      </div>
      <div className="font-mono text-lg font-semibold mt-1">{value}</div>
    </div>
  );
}


// ============================================================
// Sparklines — production vs weather forecast
// ============================================================
function PerformanceSeries({ series, accent }) {
  const s = series || {};
  if (!s.has_data) {
    return (
      <div className="bg-white border border-[var(--line)] rounded-md p-6 text-center" data-testid="share-series-empty">
        <Activity size={20} className="inline muted mb-2" />
        <div className="font-medium text-sm">Production data not yet available</div>
        <div className="muted text-xs mt-1">
          Sparklines appear once a monitoring source (SolarEdge / Solis / MeterOnline) is connected and data starts flowing.
        </div>
      </div>
    );
  }

  const monthly = s.monthly || [];
  const daily = s.daily || [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="share-series">
      {monthly.length > 0 && (
        <div className="bg-white border border-[var(--line)] rounded-md p-4 lg:col-span-2" data-testid="share-series-monthly">
          <div className="flex items-center justify-between mb-2">
            <div className="label flex items-center gap-1.5" style={{ color: accent }}>
              <TrendingUp size={13} /> Last 12 months
            </div>
            <div className="text-[10px] muted">Actual vs weather forecast (kWh)</div>
          </div>
          <div style={{ width: "100%", height: 180 }}>
            <ResponsiveContainer>
              <LineChart data={monthly} margin={{ top: 6, right: 24, bottom: 4, left: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EEF0F6" />
                <XAxis dataKey="month" interval={0} tick={{ fontSize: 9, fill: "#7A82A6" }} tickMargin={4} tickFormatter={(m) => (/^\d{4}-\d{2}$/.test(m || "") ? `${m.slice(5, 7)}/${m.slice(0, 4)}` : m)} />
                <YAxis tick={{ fontSize: 10, fill: "#7A82A6" }} width={52} tickFormatter={(v) => Math.round(v).toLocaleString("en-GB")} />
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 4 }} labelFormatter={(m) => (/^\d{4}-\d{2}$/.test(m || "") ? `${m.slice(5, 7)}/${m.slice(0, 4)}` : m)} formatter={(v) => `${Number(v).toLocaleString()} kWh`} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Line type="monotone" dataKey="actual_kwh" stroke={accent} strokeWidth={2} name="Actual" dot={{ r: 2 }} />
                <Line type="monotone" dataKey="estimated_kwh" stroke="#B33C86" strokeWidth={1.5} strokeDasharray="4 4" name="Forecast" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {daily.length > 0 && (
        <div className="bg-white border border-[var(--line)] rounded-md p-4" data-testid="share-series-daily">
          <div className="flex items-center justify-between mb-2">
            <div className="label flex items-center gap-1.5" style={{ color: accent }}>
              <Activity size={13} /> Last 30 days
            </div>
            <div className="text-[10px] muted">Daily kWh</div>
          </div>
          <div style={{ width: "100%", height: 180 }}>
            <ResponsiveContainer>
              <LineChart data={daily} margin={{ top: 6, right: 8, bottom: 4, left: 4 }}>
                <XAxis dataKey="date" tick={{ fontSize: 8, fill: "#7A82A6" }} tickMargin={4} minTickGap={14} tickFormatter={ukShort} />
                <YAxis tick={{ fontSize: 9, fill: "#7A82A6" }} width={44} tickFormatter={(v) => Math.round(v).toLocaleString("en-GB")} />
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 4 }} labelFormatter={fmtDateGB} formatter={(v) => `${Number(v).toLocaleString()} kWh`} />
                <Line type="monotone" dataKey="actual_kwh" stroke={accent} strokeWidth={2} name="Actual" dot={false} />
                <Line type="monotone" dataKey="estimated_kwh" stroke="#B33C86" strokeWidth={1.5} strokeDasharray="4 4" name="Forecast" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {monthly.length === 0 && daily.length === 0 && (
        <div className="lg:col-span-3 bg-white border border-[var(--line)] rounded-md p-6 text-center muted text-sm italic">
          No data points to plot.
        </div>
      )}
    </div>
  );
}

